import React, { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
  DialogActions,
  useTheme,
  useMediaQuery,
  Box,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Restore as RestoreIcon,
  PersonAddAlt as AddAttendeeIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import {
  Attendee,
  Schedule,
  PendingEnrollment,
} from "../../../redux/types/types";
import {
  getAttendeeAccessDisplay,
  hasClassAccess,
} from "../../../utils/specialClassAccess";
import { useDispatch } from "react-redux";
import {
  createAttendeeAbsences,
  deleteAttendeeAbsences,
} from "../../../redux/enrollment/enrollmentOperations";
import { AppDispatch, RootState } from "../../../redux/store";
import { getProfile } from "../../../redux/auth/authOperations";
import {
  fetchClassesForAttendance,
  deleteScheduleCancellation,
} from "../../../redux/admin/adminOperations";
import { getActiveAttendees } from "../../MySchedules/MySchedules";
import { useSelector } from "react-redux";
import Notiflix from "notiflix";
import EnrollmentModal from "../../MyClasses/EnrollmentModal/EnrollmentModal";
import AttendeeImportantInfoButton from "../../Attendees/AttendeeImportantInfoButton";
import ManualPaymentDialog from "../ManualPaymentDialog";
import {
  getPendingEnrollmentsByClassApi,
  removePendingEnrollmentApi,
} from "../../../utils/api/attendees";

interface EventDialogProps {
  isOpen: boolean;
  onClose: () => void;
  event: {
    id: string;
    title: string;
    start: Date;
    end: Date;
    extendedProps: {
      location: string;
      type: string;
      attendees: Attendee[];
      schedule: Schedule;
    };
  } | null;
}

export default function EventDialogInstructor({
  isOpen,
  onClose,
  event,
}: EventDialogProps) {
  const theme = useTheme();
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const [attendanceState, setAttendanceState] = useState<{
    [key: number]: boolean;
  }>({});
  const [initialAttendanceState, setInitialAttendanceState] = useState<{
    [key: number]: boolean;
  }>({});
  const [isEventStarted, setIsEventStarted] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isAdmin = useSelector((state: RootState) => state.auth.isAdmin);
  const userClasses = useSelector(
    (state: RootState) => state.auth.user.classes || [],
  );
  const adminClasses = useSelector(
    (state: RootState) => state.admin.classesAttendance || [],
  );
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [isManualPaymentDialogOpen, setIsManualPaymentDialogOpen] =
    useState(false);
  const [selectedAttendeeForPayment, setSelectedAttendeeForPayment] =
    useState<Attendee | null>(null);
  const [pendingEnrollments, setPendingEnrollments] = useState<
    PendingEnrollment[]
  >([]);
  const [loadingPendingEnrollments, setLoadingPendingEnrollments] =
    useState(false);
  // Track existing attendee IDs to mark newly enrolled attendees as absent by default
  const [existingAttendeeIds, setExistingAttendeeIds] = useState<Set<number>>(
    new Set(),
  );

  const fetchPendingEnrollments = async (classId: number) => {
    setLoadingPendingEnrollments(true);
    try {
      const response = await getPendingEnrollmentsByClassApi(classId);
      setPendingEnrollments(
        response.data.filter((e: any) =>
          e.scheduleIds.includes(event?.extendedProps.schedule.id!),
        ),
      );
    } catch (error) {
      console.error("Failed to fetch pending enrollments:", error);
      setPendingEnrollments([]);
    } finally {
      setLoadingPendingEnrollments(false);
    }
  };

  // Function to refresh admin class data when needed
  const refreshAdminClassData = async (classId?: number) => {
    if (isAdmin && classId) {
      try {
        // For attendance dialog, we need to fetch classes with attendance data
        // Get a date range around the current event to ensure we have the attendance data
        const eventDate = event?.start || new Date();
        const startDate = new Date(eventDate);
        startDate.setMonth(startDate.getMonth() - 1); // 1 month before
        const endDate = new Date(eventDate);
        endDate.setMonth(endDate.getMonth() + 2); // 2 months after

        await dispatch(
          fetchClassesForAttendance({
            page: 1,
            limit: 1000,
            sortBy: "id",
            sortOrder: "asc",
            search: "",
            startDate: startDate.toISOString().split("T")[0],
            endDate: endDate.toISOString().split("T")[0],
          }),
        );
      } catch (error) {
        console.error("Failed to refresh admin class data:", error);
      }
    }
  };

  // Function to get fresh attendee data from Redux store
  const getFreshAttendees = (): Attendee[] => {
    if (!event) return [];

    // For admin users, use admin classes, for instructors use user classes
    const classesData = isAdmin ? adminClasses : userClasses;

    if (!classesData) return event?.extendedProps.attendees || [];

    const currentClass = classesData.find(
      (cls: any) => cls.id === event.extendedProps.schedule.class?.id,
    );

    if (!currentClass) return event.extendedProps.attendees || [];

    const currentSchedule = currentClass.schedules?.find(
      (schedule: any) => schedule.id === event.extendedProps.schedule.id,
    );

    if (!currentSchedule) return event.extendedProps.attendees || [];

    // Use getActiveAttendees to get the current attendees for this date
    return getActiveAttendees(event.start, currentSchedule);
  };

  // Helper function to update attendance state with new attendees
  const updateAttendanceStateWithNewAttendees = async (
    attendeesBeforeRefresh: Attendee[],
  ) => {
    if (!event) return;

    const attendeeIdsBefore = new Set(attendeesBeforeRefresh.map((a) => a.id));
    const freshAttendees = getFreshAttendees();
    const newAttendeeIds: number[] = [];

    const updatedState = freshAttendees.reduce(
      (acc, attendee) => {
        // If this is a newly enrolled attendee (not in the original list), mark as absent
        if (
          !existingAttendeeIds.has(attendee.id) &&
          !attendeeIdsBefore.has(attendee.id)
        ) {
          acc[attendee.id] = false; // Mark new attendees as absent (unchecked)
          newAttendeeIds.push(attendee.id);
        } else {
          // Keep existing attendance state for attendees who were already enrolled
          acc[attendee.id] = attendanceState[attendee.id] ?? true;
        }
        return acc;
      },
      {} as { [key: number]: boolean },
    );

    setAttendanceState(updatedState);

    // Create absences for newly enrolled attendees to ensure they show as absent
    if (newAttendeeIds.length > 0) {
      const [, scheduleId] = event.id.split("-");
      try {
        await dispatch(
          createAttendeeAbsences({
            scheduleId: parseInt(scheduleId),
            date: event.start,
            attendeeIds: newAttendeeIds,
          }),
        );
      } catch (error) {
        console.error("Failed to create absences for new attendees:", error);
      }

      // Ensure new attendees are marked as absent regardless of absence creation result
      const finalState = { ...updatedState };
      newAttendeeIds.forEach((id) => {
        finalState[id] = false;
      });
      setAttendanceState(finalState);
      setInitialAttendanceState(finalState); // Update initial state as well
    }

    // Update the existing attendee IDs to include the new ones
    setExistingAttendeeIds(new Set(freshAttendees.map((a) => a.id)));
  };

  useEffect(() => {
    if (event) {
      const freshAttendees = getFreshAttendees();

      // Track existing attendee IDs to identify newly enrolled ones later
      setExistingAttendeeIds(new Set(freshAttendees.map((a) => a.id)));

      // Always use freshAttendees.absences which comes from Redux state
      // This ensures we always have the latest absence data
      const initialState = freshAttendees.reduce(
        (acc, attendee) => {
          acc[attendee.id] = !attendee.absences?.some((absence) => {
            return (
              new Date(absence.date).toDateString() ===
              event.start.toDateString()
            );
          });
          return acc;
        },
        {} as { [key: number]: boolean },
      );

      // Only update state if there are actual changes to prevent unnecessary re-renders
      const stateKeys = Object.keys(attendanceState).map(Number);
      const initialStateKeys = Object.keys(initialState || {}).map(Number);

      const hasStateChanged =
        stateKeys.length !== initialStateKeys.length ||
        stateKeys.some(
          (id) => attendanceState[id] !== (initialState || {})[id],
        ) ||
        initialStateKeys.some(
          (id) => (initialState || {})[id] !== attendanceState[id],
        );

      if (hasStateChanged) {
        setAttendanceState(initialState || {});
        setInitialAttendanceState(initialState || {});
      }

      const now = new Date();
      setIsEventStarted(now >= event.start);

      // Fetch pending enrollments for this class when dialog opens or event changes
      if (event.extendedProps.schedule.class?.id) {
        fetchPendingEnrollments(event.extendedProps.schedule.class.id);
      }
    }
    // eslint-disable-next-line
  }, [event, isAdmin, userClasses, adminClasses]);

  // Refresh data when dialog is opened
  useEffect(() => {
    if (isOpen && event?.extendedProps.schedule.class?.id) {
      // Refresh profile data to get latest attendee information
      dispatch(getProfile()).then(() => {
        // Refresh pending enrollments after profile data is updated
        fetchPendingEnrollments(event.extendedProps.schedule.class!.id);
      });

      // Refresh admin class data if user is admin
      if (isAdmin && event?.extendedProps.schedule.class?.id) {
        refreshAdminClassData(event.extendedProps.schedule.class.id);
      }
    }
    // eslint-disable-next-line
  }, [isOpen, event?.extendedProps.schedule.class?.id, dispatch, isAdmin]);

  const handleAttendanceChange = (attendeeId: number) => {
    if (!isEventStarted) return;

    setAttendanceState((prevState) => ({
      ...prevState,
      [attendeeId]: !prevState[attendeeId],
    }));
  };

  const handleSaveAttendance = async () => {
    if (!event || !isEventStarted) return;

    //eslint-disable-next-line
    const [classId, scheduleId] = event.id.split("-");

    // Only process attendees whose attendance status has changed
    const changedAttendees = Object.entries(attendanceState).filter(
      ([attendeeId, currentStatus]) => {
        const initialStatus = initialAttendanceState[parseInt(attendeeId)];
        return initialStatus !== currentStatus;
      },
    );

    if (changedAttendees.length === 0) {
      // No changes, just close the dialog
      onClose();
      return;
    }

    try {
      const absentAttendees = changedAttendees
        .filter(([_, isPresent]) => !isPresent)
        .map(([id]) => parseInt(id));
      const presentAttendees = changedAttendees
        .filter(([_, isPresent]) => isPresent)
        .map(([id]) => parseInt(id));

      if (absentAttendees.length > 0) {
        await dispatch(
          createAttendeeAbsences({
            scheduleId: parseInt(scheduleId),
            date: event.start,
            attendeeIds: absentAttendees,
          }),
        );
      }

      if (presentAttendees.length > 0) {
        await dispatch(
          deleteAttendeeAbsences({
            scheduleId: parseInt(scheduleId),
            date: event.start,
            attendeeIds: presentAttendees,
          }),
        );
      }

      // Update initial state to reflect saved changes
      setInitialAttendanceState({ ...attendanceState });

      // Close the dialog first to maintain visual state
      onClose();

      // Refresh all relevant data after dialog is closed
      await dispatch(getProfile());

      // Refresh admin class data if user is admin
      if (isAdmin && event?.extendedProps.schedule.class?.id) {
        await refreshAdminClassData(event.extendedProps.schedule.class.id);
      }

      // Refresh pending enrollments as attendance changes might affect enrollment status
      if (event?.extendedProps.schedule.class?.id) {
        fetchPendingEnrollments(event.extendedProps.schedule.class!.id);
      }
    } catch (error) {
      console.error("Failed to save attendance:", error);
      // Revert to initial state on error
      setAttendanceState({ ...initialAttendanceState });
    }
  };

  if (!event) return null;

  const weekdays = [
    intl.formatMessage({ id: "admin.dashboard.sunday" }),
    intl.formatMessage({ id: "admin.dashboard.monday" }),
    intl.formatMessage({ id: "admin.dashboard.tuesday" }),
    intl.formatMessage({ id: "admin.dashboard.wednesday" }),
    intl.formatMessage({ id: "admin.dashboard.thursday" }),
    intl.formatMessage({ id: "admin.dashboard.friday" }),
    intl.formatMessage({ id: "admin.dashboard.saturday" }),
  ];

  const formatTime = (date: Date) => {
    return intl.formatTime(date, {
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    });
  };

  if (!event.extendedProps.schedule.date) {
    event.extendedProps.attendees = getActiveAttendees(
      event.start,
      event.extendedProps.schedule,
    );
  }

  const handleRestore = async () => {
    const id = event.extendedProps.schedule.CancelledSchedule?.find(
      (cs) =>
        new Date(event?.start).setHours(0, 0, 0, 0) ===
        new Date(cs.date).setHours(0, 0, 0, 0),
    )?.id;

    if (typeof id === "number") await dispatch(deleteScheduleCancellation(id));
    Notiflix.Notify.success(
      intl.formatMessage({
        id: "admin.attendance.Restored",
        defaultMessage: "Restored",
      }),
    );

    // Refresh all relevant data after restoration
    await dispatch(getProfile());

    // Refresh admin class data if user is admin
    if (isAdmin && event?.extendedProps.schedule.class?.id) {
      await refreshAdminClassData(event.extendedProps.schedule.class.id);
    }

    // Refresh pending enrollments as schedule restoration might affect enrollment status
    if (event?.extendedProps.schedule.class?.id) {
      fetchPendingEnrollments(event.extendedProps.schedule.class!.id);
    }

    await onClose();
  };

  const handleEnrollClick = () => {
    setIsEnrollModalOpen(true);
  };

  const handleEnrollModalClose = () => {
    setIsEnrollModalOpen(false);
  };

  const handleManualPaymentClick = (attendee: Attendee) => {
    setSelectedAttendeeForPayment(attendee);
    setIsManualPaymentDialogOpen(true);
  };

  const handleManualPaymentClose = () => {
    setIsManualPaymentDialogOpen(false);
    setSelectedAttendeeForPayment(null);
  };

  const handleManualPaymentSuccess = async () => {
    // Manual payment now triggers immediate processing of pending enrollments
    // in the backend, so we can refresh the UI immediately

    // Get attendees before profile refresh to identify new ones
    const attendeesBeforeRefresh = getFreshAttendees();

    // Store the selected attendee ID and preserve current attendance state for existing attendees
    const enrolledAttendeeId = selectedAttendeeForPayment?.id;
    const preservedAttendanceState = { ...attendanceState };

    // Refresh the profile to get updated attendee data
    await dispatch(getProfile());

    // Refresh admin class data if user is admin
    if (isAdmin && event?.extendedProps.schedule.class?.id) {
      await refreshAdminClassData(event.extendedProps.schedule.class.id);
    }

    // Update attendance state with new attendees
    await updateAttendanceStateWithNewAttendees(attendeesBeforeRefresh);

    // Restore the preserved state for all existing attendees and only set new enrollees as unchecked
    if (enrolledAttendeeId) {
      const wasExistingAttendee = attendeesBeforeRefresh.some(
        (a) => a.id === enrolledAttendeeId,
      );

      if (wasExistingAttendee) {
        // For existing attendees, restore their original checkbox state
        setAttendanceState((prevState) => ({
          ...prevState,
          [enrolledAttendeeId]: preservedAttendanceState[enrolledAttendeeId],
        }));
      } else {
        // This was a newly enrolled attendee (from pending enrollments), mark as unchecked (absent)
        setAttendanceState((prevState) => ({
          ...prevState,
          [enrolledAttendeeId]: false,
        }));
      }
    }

    // Refresh pending enrollments list after payment processing
    if (event?.extendedProps.schedule.class?.id) {
      fetchPendingEnrollments(event.extendedProps.schedule.class!.id);
    }
  };
  const handlePendingEnrollmentPayment = (
    pendingEnrollment: PendingEnrollment,
  ) => {
    setSelectedAttendeeForPayment(pendingEnrollment.attendee);
    setIsManualPaymentDialogOpen(true);
  };

  const removePendingEnrollment = async (
    pendingEnrollment: PendingEnrollment,
  ) => {
    try {
      await removePendingEnrollmentApi(
        pendingEnrollment.attendeeId,
        pendingEnrollment.classId,
      );
      setPendingEnrollments((prev) =>
        prev.filter((pe) => pe.id !== pendingEnrollment.id),
      );

      Notiflix.Notify.success(
        intl.formatMessage({
          id: "mySchedules.pendingEnrollmentRemoved",
          defaultMessage: "Pending enrollment removed successfully",
        }),
      );
    } catch (error) {
      console.error("Failed to remove from pending enrollments:", error);
      Notiflix.Notify.failure(
        intl.formatMessage({
          id: "mySchedules.pendingEnrollmentRemoveError",
          defaultMessage: "Failed to remove from pending enrollments",
        }),
      );
    }
  };

  const handleRemoveFromPendingEnrollments = (
    pendingEnrollment: PendingEnrollment,
  ) => {
    Notiflix.Confirm.show(
      intl.formatMessage({
        id: "mySchedules.pendingEnrollmentRemoveConfirmTitle",
        defaultMessage: "Remove pending enrollment?",
      }),
      intl.formatMessage({
        id: "mySchedules.pendingEnrollmentRemoveConfirmMessage",
        defaultMessage:
          "Are you sure you want to remove this attendee from pending enrollments?",
      }),
      intl.formatMessage({ id: "common.yes", defaultMessage: "Yes" }),
      intl.formatMessage({ id: "common.no", defaultMessage: "No" }),
      () => {
        void removePendingEnrollment(pendingEnrollment);
      },
    );
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pr: 6 }}>
          <FormattedMessage
            id="mySchedules.attendance"
            defaultMessage="Attendance"
          />
          {", "}
          {intl.formatDate(event.start, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}

          <Tooltip
            title={intl.formatMessage({
              id: "myClasses.enrollAttendee",
            })}
            sx={{
              marginLeft: "10px",
            }}
          >
            <IconButton
              color="success"
              onClick={() => handleEnrollClick()}
              size="small"
            >
              <AddAttendeeIcon />
            </IconButton>
          </Tooltip>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            {weekdays[event.start.getDay()]}, {formatTime(event.start)}
            {" - "}
            {formatTime(event.end)}
          </Typography>
          {Boolean(
            event?.start &&
              Boolean(
                event.extendedProps.schedule.CancelledSchedule?.find(
                  (cs) =>
                    new Date(event?.start).setHours(0, 0, 0, 0) ===
                    new Date(cs.date).setHours(0, 0, 0, 0),
                ),
              ),
          ) ? (
            <>
              <Box sx={{ display: "flex" }}>
                <Typography variant="h3" component="div" color={"red"}>
                  {intl.formatMessage({
                    id: "upcomingClasses.Cancelled",
                    defaultMessage: "Cancelled",
                  })}
                </Typography>
                {Boolean(
                  new Date().setHours(0, 0, 0, 0) <
                    new Date(event?.start).setHours(0, 0, 0, 0),
                ) &&
                  isAdmin && (
                    <Button onClick={handleRestore}>
                      <RestoreIcon sx={{ width: "50px", height: "50px" }} />
                    </Button>
                  )}
              </Box>
              <Typography variant="h6" component="div">
                {
                  event.extendedProps.schedule.CancelledSchedule?.find(
                    (cs) =>
                      new Date(event?.start).setHours(0, 0, 0, 0) ===
                      new Date(cs.date).setHours(0, 0, 0, 0),
                  )?.comment
                }
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                <FormattedMessage
                  id="mySchedules.attendees"
                  defaultMessage="Attendees"
                />
                :
              </Typography>
              <Grid container spacing={2}>
                {getFreshAttendees().map(
                  (attendee: Attendee, index: number) => (
                    <React.Fragment key={attendee.id}>
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={attendanceState[attendee.id] ?? true}
                                onChange={() =>
                                  handleAttendanceChange(attendee.id)
                                }
                                disabled={
                                  !isEventStarted ||
                                  (() => {
                                    // If attendee is already checked as present, don't disable
                                    if (attendanceState[attendee.id])
                                      return false;

                                    const classesData = isAdmin
                                      ? adminClasses
                                      : userClasses;
                                    const currentClass = classesData?.find(
                                      (cls: any) =>
                                        cls.id ===
                                        event?.extendedProps.schedule.class?.id,
                                    );

                                    if (!currentClass) return true;

                                    // For requiresPayment classes, ALL attendees (PermisS and Other) need access
                                    if (currentClass.requiresPayment) {
                                      return !hasClassAccess(
                                        attendee,
                                        currentClass,
                                      );
                                    }

                                    // For non-requiresPayment classes, only Other status needs to check access
                                    if (attendee.permisStatus === "Other") {
                                      return !hasClassAccess(
                                        attendee,
                                        currentClass,
                                      );
                                    }

                                    // PermisS attendees with non-requiresPayment classes can always attend
                                    return false;
                                  })()
                                }
                              />
                            }
                            label={
                              <Box>
                                <Typography component="span">
                                  {`${attendee.firstName} ${attendee.lastName}`}
                                </Typography>
                                <Typography component="div">
                                  {`${intl.formatMessage({
                                    id: "admin.dashboard.class.status",
                                  })}: ${
                                    (attendee.permisStatus === "PermisS" &&
                                      intl.formatMessage({
                                        id: "attendee.permisStatus.permisS",
                                      })) ||
                                    (attendee.permisStatus === "Other" &&
                                      intl.formatMessage({
                                        id: "attendee.permisStatus.other",
                                      }))
                                  }`}
                                </Typography>
                                {(() => {
                                  const classesData = isAdmin
                                    ? adminClasses
                                    : userClasses;
                                  const currentClass = classesData?.find(
                                    (cls: any) =>
                                      cls.id ===
                                      event?.extendedProps.schedule.class?.id,
                                  );
                                  // Show for Other status or if class is special/requiresPayment
                                  if (
                                    attendee.permisStatus === "Other" ||
                                    currentClass?.isSpecial ||
                                    currentClass?.requiresPayment
                                  ) {
                                    return (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        component="div"
                                        sx={{ fontSize: "0.75rem" }}
                                      >
                                        {getAttendeeAccessDisplay(
                                          attendee,
                                          currentClass,
                                          intl.formatMessage,
                                        )}
                                      </Typography>
                                    );
                                  }
                                  return null;
                                })()}
                                <AttendeeImportantInfoButton
                                  attendee={attendee}
                                />
                              </Box>
                            }
                          />
                          {(() => {
                            // Get current class to check if payment is required
                            const classesData = isAdmin
                              ? adminClasses
                              : userClasses;
                            const currentClass = classesData?.find(
                              (cls: any) =>
                                cls.id ===
                                event?.extendedProps.schedule.class?.id,
                            );

                            // Show manual payment button for:
                            // - Other status (always)
                            // - PermisS with ANY requiresPayment class (special or regular)
                            const shouldShowPaymentButton =
                              attendee.permisStatus === "Other" ||
                              (attendee.permisStatus === "PermisS" &&
                                currentClass?.requiresPayment);

                            return shouldShowPaymentButton ? (
                              <Tooltip
                                title={intl.formatMessage({
                                  id: "manualPayment.title",
                                })}
                              >
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    handleManualPaymentClick(attendee)
                                  }
                                >
                                  <PaymentIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null;
                          })()}
                        </Box>
                      </Grid>
                      {!isMobile &&
                        index % 2 === 1 &&
                        index !== getFreshAttendees().length - 1 && (
                          <Grid item xs={12}>
                            <Divider />
                          </Grid>
                        )}
                    </React.Fragment>
                  ),
                )}
              </Grid>

              {/* Pending Enrollments Section */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  <FormattedMessage
                    id="mySchedules.pendingEnrollments"
                    defaultMessage="Pending Enrollments"
                  />
                  :
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  <FormattedMessage
                    id="mySchedules.pendingEnrollmentsDescription"
                    defaultMessage="Attendees waiting for payment to complete enrollment"
                  />
                </Typography>
                {loadingPendingEnrollments ? (
                  <Typography variant="body2">Loading...</Typography>
                ) : pendingEnrollments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                      id="mySchedules.noPendingEnrollments"
                      defaultMessage="No pending enrollments"
                    />
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {pendingEnrollments.map((pendingEnrollment) => (
                      <Grid item xs={12} sm={6} key={pendingEnrollment.id}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 1,
                            border: "1px solid",
                            borderColor: "warning.main",
                            borderRadius: 1,
                            backgroundColor: "warning.light",
                            color: "warning.contrastText",
                          }}
                        >
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="body2">
                              {pendingEnrollment.attendee.firstName}{" "}
                              {pendingEnrollment.attendee.lastName}
                            </Typography>
                            <AttendeeImportantInfoButton
                              attendee={pendingEnrollment.attendee}
                            />
                          </Box>
                          {isAdmin && (
                            <Tooltip
                              title={intl.formatMessage({
                                id: "mySchedules.removeFromPendingEnrollmentsTooltip",
                                defaultMessage:
                                  "Remove from pending enrollments",
                              })}
                            >
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  handleRemoveFromPendingEnrollments(
                                    pendingEnrollment,
                                  )
                                }
                                sx={{
                                  backgroundColor: "background.paper",
                                  "&:hover": {
                                    backgroundColor: "grey.100",
                                  },
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip
                            title={intl.formatMessage({
                              id: "mySchedules.createManualPaymentTooltip",
                              defaultMessage:
                                "Create manual payment and enroll attendee",
                            })}
                          >
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                handlePendingEnrollmentPayment(
                                  pendingEnrollment,
                                )
                              }
                              sx={{
                                backgroundColor: "background.paper",
                                "&:hover": {
                                  backgroundColor: "grey.100",
                                },
                              }}
                            >
                              <PaymentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveAttendance}
            disabled={!isEventStarted}
            fullWidth={isMobile}
          >
            <FormattedMessage
              id="common.save"
              defaultMessage="Save Attendance"
            />
          </Button>
        </DialogActions>
      </Dialog>
      <EnrollmentModal
        open={isEnrollModalOpen}
        modalClose={handleEnrollModalClose}
        onClose={() => setIsEnrollModalOpen(false)}
        classItem={event.extendedProps.schedule.class || null}
        onEnrollmentSuccess={async () => {
          // Get attendees before profile refresh to identify new ones
          const attendeesBeforeRefresh = getFreshAttendees();

          await dispatch(getProfile());

          // Refresh admin class data if user is admin
          if (isAdmin && event?.extendedProps.schedule.class?.id) {
            await refreshAdminClassData(event.extendedProps.schedule.class.id);
          }

          // Update attendance state with new attendees
          await updateAttendanceStateWithNewAttendees(attendeesBeforeRefresh);

          // Refresh pending enrollments as new enrollment might affect the list
          if (event?.extendedProps.schedule.class?.id) {
            fetchPendingEnrollments(event.extendedProps.schedule.class!.id);
          }
        }}
        isAttendance={true}
      />
      {selectedAttendeeForPayment && (
        <ManualPaymentDialog
          open={isManualPaymentDialogOpen}
          onClose={handleManualPaymentClose}
          attendee={selectedAttendeeForPayment}
          classId={event?.extendedProps.schedule.class?.id}
          onManualPaymentCreated={handleManualPaymentSuccess}
        />
      )}
    </>
  );
}
